import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import { PRICING_META } from '../data/meta'

const DEFAULT_PLANS = [
  {
    name: 'Starter',
    price: '15,000',
    period: 'one time setup',
    maintenance: '3,000',
    badge: '',
    featured: false,
    features: [
      'TRED-EX AI',
      'Basic setup and training',
      'Embed on your existing website',
      'Email support',
      '30-day optimization period',
      'Basic analytics dashboard',
    ],
    notIncluded: [
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantee',
    ]
  },
  {
    name: 'Growth',
    price: '35,000',
    period: 'one time setup',
    maintenance: '7,000',
    badge: 'Most Popular',
    featured: true,
    features: [
      '3-5 AI features of your choice',
      'Advanced setup and integrations',
      'Priority support (4hr response)',
      'Monthly optimization & tuning',
      'Performance analytics dashboard',
      'WhatsApp & email integration',
      'A/B testing for chatbot flows',
    ],
    notIncluded: [
      'Custom AI model training',
      'Dedicated account manager',
      'SLA guarantee',
    ]
  },
  {
    name: 'Enterprise',
    price: '1,50,000',
    period: 'one time setup',
    maintenance: '25,000',
    badge: '',
    featured: false,
    features: [
      'Full AI stack (all features)',
      'Custom integrations & APIs',
      'Dedicated account manager',
      'SLA guarantee (99.9% uptime)',
      'Quarterly strategy reviews',
      'Advanced analytics & reporting',
      'Custom AI model training',
      'Priority feature development',
    ],
    notIncluded: []
  }
]

const FAQS = [
  {
    question: 'How long does it take to install AI features on my website?',
    answer: 'Most features go live within 7-14 days. A simple chatbot can be installed in 3-5 days. Complex integrations like custom AI models may take 2-4 weeks. We provide a detailed timeline during your free audit.'
  },
  {
    question: 'Do I need to rebuild my website?',
    answer: 'No. We add AI features to your existing website using embed codes, APIs, and JavaScript widgets. Your current design, CMS, and hosting remain untouched.'
  },
  {
    question: 'What if I already have a website?',
    answer: 'Perfect! That\'s exactly our target. We work with websites built on WordPress, Shopify, custom HTML, React, Wix, Squarespace — almost any platform. If it has a URL, we can add AI to it.'
  },
  {
    question: 'Is there a contract or lock-in?',
    answer: 'The one time setup fee is a fixed project cost. Monthly maintenance plans are month to month — cancel anytime with 30 days notice. No long term contracts.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept bank transfers, UPI, Razorpay (credit/debit cards, UPI, net banking), and PayPal for international clients. Payment is typically 50% upfront, 50% on delivery for setup; monthly maintenance is billed monthly.'
  },
  {
    question: 'Can I start with one feature and add more later?',
    answer: 'Absolutely. Many clients start with an AI chatbot and add smart search, lead qualifier, or other features in later phases. We\'ll help you plan a roadmap that fits your budget.'
  },
]

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(null)
  const [pricing, setPricing] = useState({ symbol: '₹', plans: DEFAULT_PLANS })

  useEffect(() => {
    let cancelled = false
    fetch('/api/pricing')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data || !data.plans) return
        setPricing({ symbol: data.symbol || '₹', plans: data.plans })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const plans = pricing.plans.map((plan, i) => ({
    ...DEFAULT_PLANS[i],
    price: plan.price,
    maintenance: plan.maintenance,
  }))

  return (
    <>
      <SEO
        title={PRICING_META.title}
        description={PRICING_META.description}
        canonical={PRICING_META.canonical}
        keywords={PRICING_META.keywords}
        structuredData={PRICING_META.structuredData}
      />
      <Navbar />
      <section className="pricing-page">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Pricing</span>
            <h1 className="page-title">Simple, <span className="gradient-text">Transparent Pricing</span></h1>
            <p className="page-description">
              Fixed-price packages with no hidden fees. One-time setup + monthly maintenance. Cancel anytime.
            </p>
          </div>

          <div className="pricing-grid">
            {plans.map((plan, i) => (
              <div className={`pricing-card ${plan.featured ? 'featured' : ''}`} key={i}>
                {plan.badge && <div className="pricing-badge">{plan.badge}</div>}
                <h3>{plan.name}</h3>
                <div className="pricing-amount">{pricing.symbol}{plan.price}</div>
                <div className="pricing-period">{plan.period} + {pricing.symbol}{plan.maintenance}/month maintenance</div>
                <ul className="pricing-features">
                  {plan.features.map((f, j) => (
                    <li key={j}><i className="fas fa-check"></i> {f}</li>
                  ))}
                  {plan.notIncluded.map((f, j) => (
                    <li key={j} className="pricing-not-included"><i className="fas fa-times"></i> {f}</li>
                  ))}
                </ul>
                <Link to="/contact" className={`btn ${plan.featured ? 'btn-primary' : 'btn-outline'} btn-block`}>
                  Get Started <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            ))}
          </div>

          <div className="pricing-comparison">
            <h2>Compare Plans</h2>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Starter</th>
                    <th>Growth</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>AI Features Included</td>
                    <td>1</td>
                    <td>3-5</td>
                    <td>All 15</td>
                  </tr>
                  <tr>
                    <td>Setup Time</td>
                    <td>3-5 days</td>
                    <td>7-14 days</td>
                    <td>14-30 days</td>
                  </tr>
                  <tr>
                    <td>Support</td>
                    <td>Email</td>
                    <td>Priority (4hr)</td>
                    <td>Dedicated Manager</td>
                  </tr>
                  <tr>
                    <td>Monthly Optimization</td>
                    <td>Basic</td>
                    <td>Advanced</td>
                    <td>Strategy Reviews</td>
                  </tr>
                  <tr>
                    <td>SLA Guarantee</td>
                    <td><i className="fas fa-times pricing-icon-no"></i></td>
                    <td><i className="fas fa-times pricing-icon-no"></i></td>
                    <td><i className="fas fa-check pricing-icon-yes"></i></td>
                  </tr>
                  <tr>
                    <td>Custom Integrations</td>
                    <td><i className="fas fa-times pricing-icon-no"></i></td>
                    <td><i className="fas fa-check pricing-icon-yes"></i></td>
                    <td><i className="fas fa-check pricing-icon-yes"></i></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="pricing-faq">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              {FAQS.map((faq, i) => (
                <div className="faq-item" key={i}>
                  <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span>{faq.question}</span>
                    <i className={`fas fa-chevron-down faq-chevron ${openFaq === i ? 'active' : ''}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="pricing-bottom-cta">
            <h2>Not sure which plan is right for you?</h2>
            <p>Get a free AI audit and we'll recommend the perfect package for your business needs and budget.</p>
                                    <Link to="/contact" className="btn btn-primary btn-lg">Send <i className="fas fa-paper-plane"></i></Link>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}
